// Shared file-intake state for the two surfaces that attach files to a
// project: the new-project form (NewProjectRoute) and the
// per-project Files tab (ProjectFilesRoute). Owns the picked-file queue,
// per-file kind overrides, same-name duplicate resolution (via the one
// DuplicateResolverModal), and reuse of already-attached unchanged files.
//
// Extracted so both surfaces behave identically - before this, the Files
// tab silently re-uploaded identical files and had no duplicate modal.
// The consumer renders <DuplicateResolverModal {...dupResolverProps} />,
// the reused-files hint, and the picked-files list from the returned state.
import { useCallback, useMemo, useState } from "react";

import { isJunkFilename } from "../utils/junkFilename";

import {
  getDriveFileId,
  getDriveModifiedTime,
  getDrivePath,
  DEFAULT_UPLOAD_KIND,
  type UploadRecord,
} from "../api";
import type {
  DuplicateCandidate,
  DuplicateGroup,
} from "../components/DuplicateResolverModal";

export interface ReuseUploadRef {
  upload_id: string;
  kind: string;
  original_name: string;
}

// Single source of truth for "this picked Drive file is already attached to
// the project as the SAME file AND unchanged since we last pulled it" - so it
// can be reused by reference instead of re-downloaded / re-uploaded. Used by
// both the pre-download skip (drivePickerReuse) and the post-download reconcile
// pass, so the two can never disagree. Returns the matching upload, else
// undefined. "Unchanged" = the Drive copy's modifiedTime is not newer than when
// we last fetched its bytes (last_refreshed_at, else completed_at); if either
// timestamp is unparseable we treat it as changed (re-pull, never serve stale).
const matchUnchangedAttached = (
  existing: UploadRecord[],
  name: string,
  driveFileId: string | undefined,
  driveModifiedTime: string | undefined,
): UploadRecord | undefined => {
  if (!driveFileId) return undefined;
  const attached = existing.find(
    (u) =>
      (u.original_name ?? u.upload_id) === name &&
      u.drive_file_id === driveFileId,
  );
  if (!attached) return undefined;
  const lastPulled = attached.last_refreshed_at ?? attached.completed_at;
  const driveMs = driveModifiedTime
    ? new Date(driveModifiedTime).getTime()
    : NaN;
  const pulledMs = lastPulled ? new Date(lastPulled).getTime() : NaN;
  const unchanged =
    !Number.isNaN(driveMs) && !Number.isNaN(pulledMs) && driveMs <= pulledMs;
  return unchanged ? attached : undefined;
};

export interface ProjectFileIntake {
  // Picked-but-not-yet-uploaded files (newly added / replacing).
  files: File[];
  // Add picked files (local / Drive / Gmail), running same-name dedup,
  // reuse-by-drive_file_id, and the duplicate modal when needed.
  addFiles: (picked: File[]) => void;
  onPickFiles: (picked: FileList | null) => void;
  removeFile: (i: number) => void;
  // Clear all queued state (site change, or after a submit settles).
  reset: () => void;
  // upload_ids the operator confirmed replacing (lost a same-name modal
  // pick); the consumer fires the deletes after the upload succeeds.
  pendingDeletes: string[];
  clearPendingDeletes: () => void;
  // Already-attached uploads the operator re-picked unchanged (still
  // present on the project) - counted in the submit, not re-uploaded.
  reusedAttachedUploads: UploadRecord[];
  reuseUploadsForSubmit: () => ReuseUploadRef[];
  // Props to spread onto <DrivePickerPanel> so it skips downloading
  // already-attached unchanged files (registering them as reused instead).
  drivePickerReuse: {
    alreadyImported: (doc: {
      id: string;
      name: string;
      modifiedTime?: string;
    }) => boolean;
    onAlreadyImported: (
      docs: { id: string; name: string; modifiedTime?: string }[],
    ) => void;
  };
  // Props to spread onto <DuplicateResolverModal>.
  dupResolverProps: {
    open: boolean;
    groups: DuplicateGroup[];
    onResolve: (keepIdByName: Record<string, string>) => void;
    onCancel: () => void;
  };
}

export function useProjectFileIntake(
  existingUploads: UploadRecord[] | undefined,
): ProjectFileIntake {
  const [files, setFiles] = useState<File[]>([]);
  const [pendingDeletes, setPendingDeletes] = useState<string[]>([]);
  // upload_ids of already-attached files the operator re-picked that are
  // byte-identical (same Drive file id, unchanged) - counted in the submit
  // by id, not re-uploaded, and not surfaced as a conflict.
  const [reusedUploadIds, setReusedUploadIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [dupResolution, setDupResolution] = useState<{
    groups: DuplicateGroup[];
    // candidate id -> what keeping it means (a File to queue, or an
    // already-attached upload to leave in place).
    resolved: Map<string, { name: string; file?: File; uploadId?: string }>;
    // Non-conflicting incoming files to add regardless of the picks.
    pendingAdds: File[];
  } | null>(null);

  // The already-attached uploads the operator re-picked unchanged, still
  // present on the project. Filtering by existingUploads membership drops
  // any stale id left in the Set after a file was deleted.
  const reusedAttachedUploads = useMemo(
    () =>
      (existingUploads ?? []).filter((u) => reusedUploadIds.has(u.upload_id)),
    [existingUploads, reusedUploadIds],
  );

  const reconcilePickedFiles = useCallback(
    (incomingRaw: File[]): void => {
      // Never queue OS/editor cruft or Office lock files (~$foo.docx, .DS_Store,
      // ...) - typically swept up when a folder is dropped. Mirrors the BE
      // ingest-side filter so these can't be attached from any path.
      const incoming = incomingRaw.filter((f) => !isJunkFilename(f.name));
      const existing = existingUploads ?? [];
      // Re-picking a Drive file that's already attached as the SAME Drive
      // file (matching drive_file_id) AND unchanged since we last pulled it
      // is not a conflict - reuse the existing upload (referenced at submit,
      // no re-upload) and drop it from the conflict pass. If the Drive file
      // was EDITED since we last fetched it (its modifiedTime is newer than
      // when we pulled the bytes), the operator re-picked an updated file and
      // must get the new bytes - let it fall through to the upload/conflict
      // path (which replaces the stale copy). A genuinely different same-name
      // file (other drive_file_id, or a local pick) also falls through.
      const newlyReused: string[] = [];
      const fresh = incoming.filter((f) => {
        const attached = matchUnchangedAttached(
          existing,
          f.name,
          getDriveFileId(f),
          getDriveModifiedTime(f),
        );
        if (attached) {
          newlyReused.push(attached.upload_id);
          return false;
        }
        return true;
      });
      if (newlyReused.length > 0) {
        setReusedUploadIds((prev) => new Set([...prev, ...newlyReused]));
      }
      const namesInIncoming = new Set(fresh.map((f) => f.name));
      const pendingAdds: File[] = [];
      const groups: DuplicateGroup[] = [];
      const resolved = new Map<
        string,
        { name: string; file?: File; uploadId?: string }
      >();
      let counter = 0;
      const nextId = () => `dup-cand-${counter++}`;

      for (const name of namesInIncoming) {
        const incomingForName = fresh.filter((f) => f.name === name);
        const queuedForName = files.filter((f) => f.name === name);
        const existingForName = existing.find(
          (u) => (u.original_name ?? u.upload_id) === name,
        );
        const total =
          incomingForName.length +
          queuedForName.length +
          (existingForName ? 1 : 0);
        if (total <= 1) {
          // Just this one incoming file, nothing to clash with.
          pendingAdds.push(incomingForName[0]);
          continue;
        }
        const candidates: DuplicateCandidate[] = [];
        // Track the incoming candidates so we can default to the operator's
        // just-picked version (their action signals intent to use it) rather
        // than newest-modified, which would wrongly compare a picked file's
        // content mtime against an existing upload's upload time.
        const incomingCandidates: { id: string; modifiedTime: string }[] = [];
        const addFileCandidate = (
          f: File,
          fallbackLabel: string,
          isIncoming: boolean,
        ) => {
          const id = nextId();
          resolved.set(id, { name, file: f });
          const drivePath = getDrivePath(f);
          const modifiedTime =
            getDriveModifiedTime(f) ?? new Date(f.lastModified).toISOString();
          candidates.push({
            id,
            path: drivePath,
            sourceLabel: drivePath ? "Google Drive" : fallbackLabel,
            modifiedTime,
            sizeNum: f.size,
          });
          if (isIncoming) incomingCandidates.push({ id, modifiedTime });
        };
        for (const f of incomingForName)
          addFileCandidate(f, "On your computer", true);
        for (const f of queuedForName)
          addFileCandidate(f, "Already picked, not yet submitted", false);
        if (existingForName) {
          const id = nextId();
          resolved.set(id, { name, uploadId: existingForName.upload_id });
          candidates.push({
            id,
            sourceLabel: "Already attached to this project",
            modifiedTime: existingForName.completed_at,
            sizeNum: existingForName.size_bytes,
          });
        }
        // Default to the newest just-picked version.
        const defaultId = incomingCandidates.reduce<
          { id: string; modifiedTime: string } | null
        >(
          (best, c) => (!best || c.modifiedTime > best.modifiedTime ? c : best),
          null,
        )?.id;
        groups.push({ name, candidates, defaultId });
      }

      if (groups.length === 0) {
        if (pendingAdds.length > 0) {
          setFiles((prev) => [...prev, ...pendingAdds]);
        }
        return;
      }
      setDupResolution({ groups, resolved, pendingAdds });
    },
    [files, existingUploads],
  );

  const onResolveDuplicates = useCallback(
    (keepIdByName: Record<string, string>) => {
      const res = dupResolution;
      setDupResolution(null);
      if (!res) return;
      const conflictNames = new Set(res.groups.map((g) => g.name));
      const winnerFiles: File[] = [];
      const winnerReusedIds: string[] = [];
      const toDelete: string[] = [];
      for (const g of res.groups) {
        const winner = res.resolved.get(keepIdByName[g.name]);
        if (!winner) continue;
        if (winner.file) winnerFiles.push(winner.file);
        // The operator kept the "Already attached to this project" version -
        // register it as reused so it is counted in the submit (via
        // reuseUploadsForSubmit) and counts toward enabling the submit
        // button, exactly like a re-picked unchanged Drive file.
        else if (winner.uploadId) winnerReusedIds.push(winner.uploadId);
        // If an already-attached upload shares this name and the operator
        // didn't pick it, it's being replaced - delete it after the upload.
        const existingForName = (existingUploads ?? []).find(
          (u) => (u.original_name ?? u.upload_id) === g.name,
        );
        if (existingForName && winner.uploadId !== existingForName.upload_id) {
          toDelete.push(existingForName.upload_id);
        }
      }
      // Drop every queued file whose name was in conflict (the winner - which
      // may BE the previously-queued file - is re-added), then add the
      // non-conflicting picks and the per-name winners.
      setFiles((prev) => [
        ...prev.filter((f) => !conflictNames.has(f.name)),
        ...res.pendingAdds,
        ...winnerFiles,
      ]);
      if (winnerReusedIds.length > 0) {
        setReusedUploadIds((prev) => new Set([...prev, ...winnerReusedIds]));
        // A winner can't also be pending deletion (e.g. it lost an earlier
        // modal round, then the operator picked it in this one).
        setPendingDeletes((prev) =>
          prev.filter((id) => !winnerReusedIds.includes(id)),
        );
      }
      if (toDelete.length > 0) {
        setPendingDeletes((prev) => {
          const merged = [...prev];
          for (const id of toDelete) if (!merged.includes(id)) merged.push(id);
          return merged;
        });
        // The mirror of the winner case: an attached upload the operator
        // chose to replace can't stay registered as reused (it may have been
        // registered by an earlier re-pick of the unchanged file).
        setReusedUploadIds((prev) => {
          if (!toDelete.some((id) => prev.has(id))) return prev;
          const next = new Set(prev);
          for (const id of toDelete) next.delete(id);
          return next;
        });
      }
    },
    [dupResolution, existingUploads],
  );

  const onCancelDuplicates = useCallback(() => {
    // Cancel resolves nothing: leave every conflicting name as-is, but still
    // queue the picks that had no conflict (the operator clearly wants those).
    const res = dupResolution;
    setDupResolution(null);
    if (res && res.pendingAdds.length > 0) {
      setFiles((prev) => [...prev, ...res.pendingAdds]);
    }
  }, [dupResolution]);

  const addFiles = useCallback(
    (picked: File[]) => {
      if (picked.length === 0) return;
      reconcilePickedFiles(picked);
    },
    [reconcilePickedFiles],
  );

  const onPickFiles = useCallback(
    (picked: FileList | null) => {
      if (!picked) return;
      reconcilePickedFiles(Array.from(picked));
    },
    [reconcilePickedFiles],
  );

  const removeFile = useCallback((i: number) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  }, []);

  const reset = useCallback(() => {
    setFiles([]);
    setPendingDeletes([]);
    setReusedUploadIds(new Set());
    setDupResolution(null);
  }, []);

  const clearPendingDeletes = useCallback(() => setPendingDeletes([]), []);

  // Pre-download optimisation for the Drive picker: a picked Drive file that
  // is already attached as the same drive file AND unchanged since we last
  // pulled it needn't be downloaded at all - we'll reference the existing
  // upload. Same "unchanged since last pull" rule as reconcilePickedFiles,
  // but evaluated on Drive metadata (id + modifiedTime) before any bytes are
  // fetched. The picker filters these out of its download set and reports
  // them via onAlreadyImported so we can register the reuse.
  const findUnchangedAttached = useCallback(
    (doc: { id: string; name: string; modifiedTime?: string }) =>
      matchUnchangedAttached(
        existingUploads ?? [],
        doc.name,
        doc.id,
        doc.modifiedTime,
      ),
    [existingUploads],
  );

  const drivePickerReuse = useMemo(
    () => ({
      alreadyImported: (doc: {
        id: string;
        name: string;
        modifiedTime?: string;
      }) => findUnchangedAttached(doc) !== undefined,
      onAlreadyImported: (
        docs: { id: string; name: string; modifiedTime?: string }[],
      ) => {
        const ids: string[] = [];
        for (const d of docs) {
          const attached = findUnchangedAttached(d);
          if (attached) ids.push(attached.upload_id);
        }
        if (ids.length > 0) {
          setReusedUploadIds((prev) => new Set([...prev, ...ids]));
        }
      },
    }),
    [findUnchangedAttached],
  );

  const reuseUploadsForSubmit = useCallback(
    (): ReuseUploadRef[] =>
      // Exclude platform-generated outputs: they're deliverables, not source
      // documents, and their kind (generated_artifact / procurement_comparison)
      // isn't in the BE FileKind vocabulary, so re-feeding them would either be
      // wrong or get coerced to a generic "other" input.
      reusedAttachedUploads
        .filter((u) => !u.is_agent_output)
        .map((u) => ({
          upload_id: u.upload_id,
          kind: u.kind ?? DEFAULT_UPLOAD_KIND,
          original_name: u.original_name,
        })),
    [reusedAttachedUploads],
  );

  return {
    files,
    addFiles,
    onPickFiles,
    removeFile,
    reset,
    pendingDeletes,
    clearPendingDeletes,
    reusedAttachedUploads,
    reuseUploadsForSubmit,
    drivePickerReuse,
    dupResolverProps: {
      open: dupResolution !== null,
      groups: dupResolution?.groups ?? [],
      onResolve: onResolveDuplicates,
      onCancel: onCancelDuplicates,
    },
  };
}
